import React, { Component } from "react";
import T from "prop-types";
import { Col } from "react-bootstrap";
import { Field, FormSection } from "redux-form";
import api from "APP_ROOT/api";
import pojo from "LIB/pojos";
import {
  arrayToProperCasedOptionsWithCurrentValue,
  toOptions
} from "LIB/forms";
import {
  Select,
  FormInput,
  CheckGroup,
  Check,
  FormRow,
  PhoneNumberField
} from "MODULES/shared/components/Forms";
import { CompanyStatsTooltip } from "MODULES/shared/components";
import AutoCompleteField from "MODULES/shared/components/Forms/AutoCompleteField/AutoCompleteField";
import {
  WIRE_IN_ADVANCE,
  CHECK_IN_ADVANCE,
  CREDIT_CARD,
  VENDOR_TERMS,
  CONTACT,
  BILLING_ADDRESS
} from "MODULES/companies/constants";
import * as F from "MODULES/shared/components/Forms/FormTypes";
import withFormFields from "MODULES/shared/decorators/withFormFields";
import { selectPrimaryContact } from "MODULES/companies/helpers";
import {
  COMPANY_ID,
  CONTACT_ID,
  COUNTRY,
  EMAIL,
  NAME,
  PHONE
} from "APP_ROOT/constants";
import {
  AFTER_HOURS,
  CREDIT_TERMS,
  SEND_SALES_ORDER_ACKNOWLEDGEMENT
} from "MODULES/SalesOrders/constants";
import { properCase } from "LIB/formatters/text";
import MissingFieldsWarning from "MODULES/companies/shared/components/MissingFieldsWarning";
import AddressFields from "MODULES/companies/components/AddressFields";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import DoNotExchangeWarning from "MODULES/companies/shared/components/DoNotExchangeWarning";
import CustomerFullNameRequiredWarning from "MODULES/SalesOrders/components/shared/Warnings/CustomerFullNameRequiredWarning";
import RestrictedEntityWarning from "MODULES/companies/shared/components/RestrictedEntityWarning";
import withCurrentUser from "MODULES/session/decorators/withCurrentUser";

const baseOptions = [WIRE_IN_ADVANCE, CHECK_IN_ADVANCE, CREDIT_CARD];

class CustomerSection extends Component {
  static defaultProps = {
    setPrimaryContact: true
  };

  state = {
    contacts: [],
    terms: baseOptions,
    selectedCompany: null,
    selectedContact: null,
    afterHours: this.props.afterHours
  };

  componentWillMount() {
    const { company, creditTerms } = this.props;

    if (company) {
      this.setState({ selectedCompany: company });
      this.searchContacts(company.id);

      if (company.customer_credit_terms) {
        this.addOption(company.customer_credit_terms);
      }
    }

    if (creditTerms) {
      this.addOption(creditTerms);
    }
  }

  searchCompanies = (filter, type = "detail") =>
    api.companies
      .search({ cached: true, filter, page: 1, type, limit: 20 })
      .then(resp => resp.data.results);

  searchContacts = companyId => {
    if (!companyId) {
      this.setState({ contacts: [] });
    } else {
      api.companies.contacts
        .search(companyId, {
          active: true,
          cached: true,
          page: 1,
          limit: 1000,
          sort: "name",
          reverse: false
        })
        .then(resp => {
          const contacts = resp.data.results;
          this.setState({ contacts });
          if (this.props.setPrimaryContact) {
            selectPrimaryContact(
              contacts,
              this.props.setFieldValue(CONTACT_ID)
            );
          }
        });
    }
  };

  handleCompanySelected = company => {
    this.setState({ selectedCompany: company });
    this.resetTerms();
    this.props.contact_id.input.onChange(null);
    if (company.customer_credit_terms) {
      this.updateTerms(company.customer_credit_terms);
    }

    if (this.props.onCompanySelected) {
      this.props.onCompanySelected(company);
    }
    this.searchContacts(company.id);

    if (company.contacts && company.contacts.length > 0) {
      const firstPrimaryContact = company.contacts.find(
        contact => contact.primary === true
      );

      if (firstPrimaryContact) {
        this.props.contact_id.input.onChange(firstPrimaryContact.id);
        this.setState({ selectedContact: firstPrimaryContact });
      } else {
        this.setState({ selectedContact: null });
      }
    } else {
      this.setState({ selectedContact: null });
    }
  };

  resetTerms = () => {
    this.props.credit_terms.input.onChange(null);
    this.setState({ terms: baseOptions });
  };

  addOption = option => {
    this.setState({
      terms: [option].concat(baseOptions)
    });
  };

  updateTerms = term => {
    this.addOption(term);
    this.props.credit_terms.input.onChange(term);
  };

  render() {
    const { contacts, selectedCompany, selectedContact, terms } = this.state;

    const {
      canEditCompany = true,
      contact_id: { input: { value } },
      currentUser,
      formName,
      getFieldValue
    } = this.props;

    const paymentTermOptions = arrayToProperCasedOptionsWithCurrentValue(
      currentUser.tenant.payment_terms || Object.keys(VENDOR_TERMS),
      selectedCompany && selectedCompany.customer_credit_terms
    );

    const options = paymentTermOptions.filter(f => terms.includes(f.value));
    const contactOptions = (contacts || [])
      .map(c => ({
        value: c.id,
        text: c.name
      }))
      .concat({
        text: "+ Add a Contact",
        value: "Add"
      });

    return (
      <div>
        <FormRow>
          <Col md={4}>
            <Field
              required
              name="customer_purchase_order_number"
              component={FormInput}
              label="Customer PO#"
            />
          </Col>
        </FormRow>
        <MissingFieldsWarning
          company={selectedCompany}
          contact={selectedContact}
          getFieldValue={getFieldValue}
        />
        <DoNotExchangeWarning company={selectedCompany} />
        <CustomerFullNameRequiredWarning
          name={
            (selectedContact && selectedContact.name) ||
            getFieldValue("contact.name")
          }
        />
        <RestrictedEntityWarning
          company={selectedCompany}
          allContacts
          requireApproval
        />
        <FormRow>
          <Col md={8}>
            <Field
              name={COMPANY_ID}
              label="Customer"
              labelChildren={
                selectedCompany && (
                  <React.Fragment>
                    &nbsp;&nbsp;
                    <CompanyStatsTooltip company={selectedCompany} />
                  </React.Fragment>
                )
              }
              placeholder="Search by typing a company's name or code"
              component={AutoCompleteField}
              search={filter =>
                this.searchCompanies(filter, "all_associations")}
              formatItem={result => result && result.name}
              onItemSelected={i => i && this.handleCompanySelected(i)}
              selectedItem={selectedCompany}
              disabled={!canEditCompany}
              required
            />
          </Col>
          <Col md={4}>
            <Field
              name={CREDIT_TERMS}
              component={Select}
              label="Customer Credit Terms"
              options={options}
              required
            />
          </Col>
        </FormRow>
        {selectedCompany &&
          !selectedCompany.billing_address && [
            <h4>Billing Address</h4>,
            <AddressFields
              country={getFieldValue([BILLING_ADDRESS, COUNTRY].join("."))}
              path={BILLING_ADDRESS}
              formName={formName}
              showCareOf={false}
              showRegion
              resetState
              stateProvinceRequired
            />
          ]}
        {selectedCompany &&
          !selectedCompany.phone && [
            <h4>Missing Company Fields</h4>,
            <FormRow>
              <Col md={5}>
                <PhoneNumberField
                  name={PHONE}
                  component={FormInput}
                  label="Phone Number"
                  type="tel"
                  required
                />
              </Col>
            </FormRow>,
            <h4>Contact</h4>
          ]}
        <FormRow>
          <Col md={4}>
            <Field
              name={CONTACT_ID}
              component={Select}
              label="Customer Contact"
              options={contactOptions}
              onChange={(_, val) => {
                const contact = (this.state.contacts || []).find(
                  c => Number(c.id) === Number(val)
                );

                this.setState({
                  selectedContact: contact
                });
              }}
              required
            />
          </Col>
          <Col md={4}>
            <Field
              name="cc_email_address"
              component={FormInput}
              label="Contact Email #2"
            />
          </Col>
          <Col md={4}>
            <Field
              name="third_contact_email"
              component={FormInput}
              label="Contact Email #3"
            />
          </Col>
        </FormRow>
        {value === "Add" && (
          <FormRow>
            <FormSection name={CONTACT}>
              <Col md={4}>
                <div className="form-group">
                  <Field
                    name={NAME}
                    component={FormInput}
                    label="Contact Name"
                    required
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className="form-group">
                  <Field
                    name={EMAIL}
                    component={FormInput}
                    label="Contact Email"
                    required
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className="form-group">
                  <PhoneNumberField
                    name="phone1"
                    component={FormInput}
                    label="Phone Number 1"
                    type="tel"
                  />
                </div>
              </Col>
            </FormSection>
          </FormRow>
        )}
        <FormRow>
          <Col md={4}>
            <CheckGroup justified title={properCase(AFTER_HOURS)}>
              <Field name={AFTER_HOURS} component={Check} label="Yes" />
            </CheckGroup>
          </Col>
          <Col md={5}>
            <CheckGroup justified title="Send SO Acknowledgement">
              <Field
                name={SEND_SALES_ORDER_ACKNOWLEDGEMENT}
                component={Check}
                label="Yes"
                icon="fa-eye-slash text-success"
              />
            </CheckGroup>
          </Col>
        </FormRow>
      </div>
    );
  }
}

CustomerSection.propTypes = {
  afterHours: T.bool.isRequired,
  company: T.shape({
    id: T.number.isRequired,
    customer_credit_terms: T.string
  }),
  creditTerms: T.string,
  credit_terms: F.formField(F.stringInput).isRequired,
  canEditCompany: T.bool,
  contact_id: F.formField(F.malleable).isRequired,
  formName: T.string,
  getFieldValue: T.func.isRequired,
  onCompanySelected: T.func,
  setFieldValue: T.func,
  setPrimaryContact: T.bool
};

export default withCurrentUser(
  withFieldAccessors(
    withFormFields(CustomerSection, [
      CREDIT_TERMS,
      CONTACT_ID,
      AFTER_HOURS,
      SEND_SALES_ORDER_ACKNOWLEDGEMENT
    ])
  )
);



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/shared/FormSections/CustomerSection/CustomerSection.jsx